<template>
    <div class="wall-management-page">
        <div class="settings-card">
            <h2 class="page-title">
                <Icon icon="heroicons:view-columns" width="24" height="24" />
                视频成品列表
            </h2>

            <div class="wall-toolbar">
                <div class="toolbar-left">
                    <el-input v-model="search.keyword" size="large" placeholder="按名称 / URL 搜索" clearable class="kw"
                        @keyup.enter="doSearch">
                        <template #prefix>
                            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
                        </template>
                    </el-input>

                    <el-button type="primary" size="large" @click="doSearch">
                        <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
                        搜索
                    </el-button>

                    <el-checkbox v-model="allChecked" :indeterminate="indeterminate" class="ml10" @change="toggleAll">
                        全选本页
                    </el-checkbox>

                    <el-popconfirm v-if="selectedIds.length" :title="`确认删除选中的 ${selectedIds.length} 个视频吗？`"
                        @confirm="doBatchDelete" :teleported="true">
                        <template #reference>
                            <el-button type="danger" size="large" :disabled="!selectedIds.length" :loading="batching">
                                批量删除<span v-if="selectedIds.length">（{{ selectedIds.length }}）</span>
                            </el-button>
                        </template>
                    </el-popconfirm>
                </div>

                <div class="toolbar-right">
                    <el-button class="mr8"
                        :disabled="!selectedIds.length || !list.some(r => selectedIds.includes(r.id) && r.video_url)"
                        :loading="zipMaking" @click="downloadBatchZip">
                        <Icon icon="heroicons:archive-box-arrow-down" width="18" height="18" />
                        批量打包下载ZIP
                    </el-button>

                    <el-button size="large" :loading="loading" @click="fetchList">
                        <Icon icon="heroicons:arrow-path" width="18" height="18" />
                        刷新
                    </el-button>
                </div>
            </div>

            <!-- 骨架屏 -->
            <transition-group name="fade" tag="div" class="grid" v-if="loading">
                <div v-for="n in pageSize" :key="'skeleton-' + n" class="card skeleton">
                    <div class="thumb shimmer"></div>
                    <div class="meta">
                        <div class="line shimmer"></div>
                        <div class="line short shimmer"></div>
                    </div>
                </div>
            </transition-group>

<!-- 视频卡片 -->
<transition-group name="stagger" tag="div" class="grid" v-else>
    <div v-for="it in list" :key="it.id" :class="['card', { selected: checkedMap[it.id] }]">
        <el-checkbox v-model="checkedMap[it.id]" class="checker" @change="onCheck(it)" />
        <video class="thumb portrait" :src="it.video_url" :title="it.video_name || '未命名视频'"
            preload="metadata" playsinline controls :muted="true"></video>

        <div class="meta">
            <div class="name" :title="it.video_name || ''">{{ it.video_name || '未命名视频' }}</div>
            <div class="row">
                <el-tag size="small">使用 {{ it.use_num || 0 }}</el-tag>
                <span class="time">{{ it.create_time }}</span>
            </div>
        </div>

        <div class="actions">
            <el-popconfirm title="确认删除该视频吗？" @confirm="delOne(it.id)" :teleported="true">
                <template #reference>
                    <el-button type="danger" size="small">
                        <Icon icon="heroicons:trash" width="14" height="14" />
                        删除
                    </el-button>
                </template>
            </el-popconfirm>
        </div>
    </div>
</transition-group>

            <!-- 分页 -->
            <div class="pagination-container">
                <el-pagination v-model:current-page="page" v-model:page-size="pageSize" :total="total"
                    :page-sizes="[12, 24, 36, 60]" layout="total, sizes, prev, pager, next, jumper"
                    @size-change="fetchList" @current-change="fetchList" />
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Cookies from 'js-cookie';
import config from '@/config.js';
import { saveAs } from 'file-saver'
import { Icon } from '@iconify/vue';

export default {
    name: 'AllVideosWall',
    components: { Icon },
    data() {
        return {
            loading: false,
            batching: false,
            list: [],
            total: 0,
            page: 1,
            pageSize: 10,
            search: { keyword: '' },
            zipMaking: false,
            zipProgress: 0,
            checkedMap: {},
            selectedIds: [],
            allChecked: false,
            indeterminate: false,
            hoverId: null,
            minSkeletonMs: 800,
        }
    },
    computed: {
        userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') },
        api() { return `${config.BASE_URL}/shop/api/video_cp_materials.php` },
    },
    mounted() { this.fetchList(); },
    methods: {
        async fetchList() {
            const start = Date.now();
            try {
                this.loading = true;
                const payload = {
                    action: this.search.keyword ? 'search_all' : 'list_all',
                    store_id: this.userInfo.id,
                    page: this.page,
                    page_size: this.pageSize
                };
                if (this.search.keyword) payload.keyword = this.search.keyword;

                const { data } = await axios.post(this.api, payload);
                this.list = data?.data || [];
                this.total = data?.pagination?.total || 0;

                this.checkedMap = {};
                this.selectedIds = [];
                this.syncCheckState();
            } catch (e) {
                this.$message.error('加载失败');
                console.error(e);
            } finally {
                const elapsed = Date.now() - start;
                const remain = Math.max(0, this.minSkeletonMs - elapsed);
                setTimeout(() => { this.loading = false; }, remain);
            }
        },
        doSearch() {
            this.page = 1;
            this.fetchList();
        },
        async downloadBatchZip() {
            const rows = this.list
                .filter(r => this.selectedIds.includes(r.id))
                .filter(r => r.video_url)

            if (!rows.length) {
                this.$message.warning('请选择至少一个"已合成"的视频')
                return
            }

            this.zipMaking = true
            try {
                const items = rows.map(r => ({
                    url: r.video_url,
                    filename: this.buildSafeName(`${r.video_name || 'video'}_${r.id}.mp4`)
                }))

                const res = await fetch(`${config.BASE_URL}/shop/api/zip_videos.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items })
                })

                if (!res.ok) throw new Error(`HTTP ${res.status}`)

                const blob = await res.blob()
                saveAs(blob, `videos_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.zip`)
                this.$message.success(`打包完成，共 ${rows.length} 个视频`)
            } catch (e) {
                console.error(e)
                this.$message.error('打包失败，请重试')
            } finally {
                this.zipMaking = false
                this.zipProgress = 0
            }
        },
        fetchAsBlob(url) {
            return fetch(url, { mode: 'cors' }).then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                return res.blob()
            })
        },
        buildSafeName(name) {
            return String(name)
                .replace(/[\\/:*?"<>|]/g, '_')
                .replace(/\s+/g, '_')
                .slice(0, 120)
        },
        downloadOne(row) {
            if (!row?.video_url) {
                this.$message.warning('没有可下载的视频')
                return
            }
            this.forceDownload(row.video_url, `${row.video_name || 'video'}_${row.id}.mp4`)
        },
        forceDownload(url, filename) {
            const a = document.createElement('a')
            a.href = url
            a.download = filename || 'video.mp4'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        },
        onCheck(it) {
            const on = !!this.checkedMap[it.id];
            if (on && !this.selectedIds.includes(it.id)) {
                this.selectedIds.push(it.id);
            } else if (!on) {
                this.selectedIds = this.selectedIds.filter(id => id !== it.id);
            }
            this.syncCheckState();
        },
        toggleAll(val) {
            if (!Array.isArray(this.list) || !this.list.length) return;
            this.checkedMap = val
                ? Object.fromEntries(this.list.map(it => [it.id, true]))
                : {};
            this.selectedIds = val ? this.list.map(it => it.id) : [];
            this.syncCheckState();
        },
        syncCheckState() {
            const total = this.list.length;
            const checkedCount = this.list.filter(it => this.checkedMap[it.id]).length;
            this.allChecked = total > 0 && checkedCount === total;
            this.indeterminate = checkedCount > 0 && checkedCount < total;
        },
        async delOne(id) {
            try {
                const { data } = await axios.post(this.api, {
                    action: 'delete',
                    id,
                    store_id: this.userInfo.id
                });
                if (data?.success) {
                    this.$message.success('已删除');
                    this.fetchList();
                } else {
                    this.$message.error(data?.error || '删除失败');
                }
            } catch (e) {
                this.$message.error('删除失败');
            }
        },
        async doBatchDelete() {
            if (!this.selectedIds.length) return;
            try {
                this.batching = true;
                const { data } = await axios.post(this.api, {
                    action: 'batch_delete',
                    store_id: this.userInfo.id,
                    ids: this.selectedIds
                });
                if (data?.success) {
                    this.$message.success(`批量删除成功（${data.deleted || this.selectedIds.length}）`);
                    this.fetchList();
                } else {
                    this.$message.error(data?.error || '批量删除失败');
                }
            } catch (e) {
                this.$message.error('批量删除失败');
            } finally {
                this.batching = false;
            }
        }
    }
}
</script>

<style scoped lang="scss">
.wall-management-page {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
}

.settings-card {
    background: rgba(18, 14, 33, 0.6);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    border: 1px solid rgba(157, 78, 221, 0.2);
    padding: 24px;
    width: 100%;
    box-sizing: border-box;

    @media (max-width: 768px) {
        padding: 16px;
    }

    @media (max-width: 576px) {
        padding: 12px;
    }
}

.page-title {
    font-size: 20px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 10px;

    svg {
        color: #c084fc;
    }

    @media (max-width: 768px) {
        font-size: 18px;
        margin-bottom: 16px;
    }
}

.wall-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;

    .toolbar-left {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
    }

    .toolbar-right {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .kw {
        width: 360px;
    }

    .ml10 {
        margin-left: 10px;
    }
}

.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 18px;
}

.card {
    position: relative;
    border-radius: 16px;
    overflow: hidden;  /* 恢复 hidden，保持圆角 */
    background: rgba(18, 14, 33, 0.6);
    border: 1px solid rgba(157, 78, 221, 0.25);
    box-shadow: 0 10px 22px rgba(6, 18, 36, 0.25);
    transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;

    &:hover {
        transform: translateY(-3px) scale(1.01);
        box-shadow: 0 18px 38px rgba(6, 18, 36, 0.38);
        border-color: rgba(157, 78, 221, 0.5);
    }

    /* 默认隐藏操作按钮，hover 时显示 */
    .actions {
        opacity: 0;
        transition: opacity 0.2s ease;
    }

    &:hover .actions {
        opacity: 1;
    }
}

.actions {
    position: absolute;
    right: 12px;
    top: 12px;
    z-index: 2;
}

.selected {
    border-color: rgba(0, 255, 204, 0.7) !important;
    box-shadow: 0 0 24px rgba(0, 255, 204, 0.25);
}

.checker {
    position: absolute;
    left: 12px;
    top: 12px;
    z-index: 2;
}

.thumb {
    width: 100%;
    aspect-ratio: 9 / 16;
    height: auto;
    background: #000;
    object-fit: cover;
    transition: transform .25s ease, filter .25s ease;
}

.card:hover .thumb {
    transform: scale(1.02);
    filter: saturate(1.05);
}

.meta {
    padding: 12px 14px 14px;
    color: #cdd6e4;

    .name {
        font-weight: 700;
        letter-spacing: .2px;
        color: #e6eefb;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 8px 0 2px;
    }

    .time {
        font-size: 12px;
        opacity: .7;
    }
}

.actions {
    position: absolute;
    right: 12px;
    top: 12px;
    z-index: 2;
    overflow: visible;  /* 添加这行 */
}

.skeleton {
    .thumb {
        aspect-ratio: 9 / 16;
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    }

    .meta {
        padding: 14px;
    }

    .line {
        height: 14px;
        border-radius: 6px;
        background: rgba(255, 255, 255, 0.08);
        margin-bottom: 10px;
    }

    .short {
        width: 60%;
    }
}

.shimmer {
    position: relative;
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        transform: translateX(-100%);
        background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, .12) 50%, rgba(255, 255, 255, 0) 100%);
        animation: shimmer 1.6s infinite;
    }
}

@keyframes shimmer {
    100% {
        transform: translateX(100%);
    }
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity .25s ease;
}

.stagger-enter-from {
    opacity: 0;
    transform: translateY(8px) scale(.98);
}

.stagger-enter-active {
    transition: all .28s ease;
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;

    @media (max-width: 768px) {
        justify-content: center;
    }
}

:deep(.el-pagination) {
    --el-pagination-bg-color: transparent;
    --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
    --el-pagination-hover-color: #c084fc;

    .btn-prev,
    .btn-next,
    .el-pager li {
        background: rgba(157, 78, 221, 0.1);
        border: 1px solid rgba(157, 78, 221, 0.3);
        color: #a0a0c0;
        border-radius: 8px;
        min-width: 32px;
        height: 32px;

        &:hover {
            background: rgba(157, 78, 221, 0.2);
            color: #c084fc;
        }

        &.active {
            background: linear-gradient(135deg, #6546b4, #9d4edd);
            color: #fff;
            border-color: transparent;
        }
    }

    .el-pagination__total,
    .el-pagination__jump {
        color: #a0a0c0;
    }
}

:deep(.el-input__wrapper) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 10px;
    box-shadow: none;

    &:hover {
        border-color: rgba(157, 78, 221, 0.6);
    }

    &.is-focus {
        border-color: #9d4edd;
        box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }

    .el-input__inner {
        color: #fff;
        background: transparent;

        &::placeholder {
            color: #5a5a7a;
        }
    }
}

:deep(.el-checkbox__label) {
    color: #a0a0c0;
}

:deep(.el-checkbox__inner) {
    border-color: rgba(157, 78, 221, 0.5);
    background: rgba(26, 21, 53, 0.8);
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
    background-color: #9d4edd;
    border-color: #9d4edd;
}

:deep(.el-button) {
    border-radius: 10px;
    padding: 10px 20px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
}

:deep(.el-button--primary) {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    border: none;

    &:hover {
        transform: translateY(-1px);
    }
}
</style>